import { ApiKeyUsageDetail } from '../../../../utils/openai-api';
import { hasTrustedPersonOwnerIdentity } from '../../../../utils/api-key-identity';

// ── getApiKeyUniqueId ──────────────────────────────────

export function getApiKeyUniqueId(apiKey: ApiKeyUsageDetail): string {
  const providerKey = apiKey.provider ?? 'unknown';
  return `${providerKey}:${apiKey.id}`;
}

// ── mergeApiKeysByName ─────────────────────────────────

export function mergeApiKeysByName(apiKeys: ApiKeyUsageDetail[]): ApiKeyUsageDetail[] {
  const groups = new Map<string, ApiKeyUsageDetail[]>();

  apiKeys.forEach(apiKey => {
    // For Claude Code keys with a resolved ownerEmail, use the email as the
    // grouping key so they merge with regular API keys named by the same email.
    // Regular API keys keep name-based grouping (their name IS the email).
    let key: string;
    if (apiKey.ownerEmail && hasTrustedPersonOwnerIdentity(apiKey)) {
      key = apiKey.ownerEmail.trim().toLowerCase();
    } else {
      const normalized = apiKey.name?.trim().toLowerCase();
      key = normalized && normalized.length > 0 ? normalized : getApiKeyUniqueId(apiKey);
    }
    const current = groups.get(key);
    if (current) {
      current.push(apiKey);
    } else {
      groups.set(key, [apiKey]);
    }
  });

  const merged: ApiKeyUsageDetail[] = [];

  groups.forEach((group, _key) => {
    if (group.length === 1) {
      const single = group[0];
      // Show ownerEmail instead of claude_code_* name when available
      if (
        single.ownerEmail
        && hasTrustedPersonOwnerIdentity(single)
        && !single.name?.includes('@')
      ) {
        merged.push({ ...single, name: single.ownerEmail });
      } else {
        merged.push(single);
      }
      return;
    }

    const providers = new Set<ApiKeyUsageDetail['provider']>();
    const workspaceIds = new Set<string>();
    const workspaceNames = new Set<string>();

    // For user-grouped Anthropic keys, pick the best display name:
    // prefer email-like names, then ownerEmail, then original name
    const bestName = group.find(e => e.name?.includes('@'))?.name
      ?? group.find(e => e.ownerEmail && hasTrustedPersonOwnerIdentity(e))?.ownerEmail
      ?? group[0].name;

    const aggregated: ApiKeyUsageDetail = {
      ...group[0],
      id: group[0].id,
      name: bestName,
      requests: 0,
      numberOfRequests: 0,
      contextTokens: 0,
      generatedTokens: 0,
      cachedTokens: 0,
      cost: 0,
      cachedCost: 0,
      cachedPercentage: 0,
      costPer100Requests: 0,
      modelCosts: {},
      dailyModelCosts: {},
      modelUsage: {},
      projectIds: [],
      totalCost: 0,
      provider: 'mixed'
    };

    let earliest: string | null = null;
    let latest: string | null = null;
    let maxDays = 0;
    let isActive = false;
    const sourceIds = new Set<string>();

    group.forEach(entry => {
      if (entry.provider) {
        providers.add(entry.provider);
      }

      if (entry.sourceIds && entry.sourceIds.length > 0) {
        entry.sourceIds.forEach(id => sourceIds.add(id));
      } else {
        sourceIds.add(entry.id);
      }

      if (entry.projectId) {
        workspaceIds.add(entry.projectId);
      }
      entry.projectIds?.forEach(id => workspaceIds.add(id));
      if (entry.projectName) {
        workspaceNames.add(entry.projectName);
      }

      aggregated.requests += entry.requests;
      aggregated.numberOfRequests += entry.numberOfRequests ?? entry.requests;
      aggregated.contextTokens += entry.contextTokens;
      aggregated.generatedTokens += entry.generatedTokens;
      aggregated.cachedTokens += entry.cachedTokens;
      aggregated.cost += entry.cost;
      aggregated.cachedCost += entry.cachedCost;
      aggregated.totalCost = (aggregated.totalCost ?? 0) + (entry.totalCost ?? entry.cost);

      Object.entries(entry.modelCosts || {}).forEach(([model, value]) => {
        aggregated.modelCosts[model] = (aggregated.modelCosts[model] ?? 0) + value;
      });

      Object.entries(entry.modelUsage || {}).forEach(([modelName, usage]) => {
        const existing = aggregated.modelUsage[modelName];
        const provider = usage.provider ?? entry.provider;
        if (!existing) {
          aggregated.modelUsage[modelName] = { ...usage, provider };
        } else {
          existing.contextTokens += usage.contextTokens;
          existing.generatedTokens += usage.generatedTokens;
          existing.cachedTokens += usage.cachedTokens;
          existing.cost += usage.cost;
          existing.numberOfRequests += usage.numberOfRequests;
          if (provider && existing.provider && provider !== existing.provider) {
            existing.provider = 'mixed';
          } else if (provider && !existing.provider) {
            existing.provider = provider;
          }
        }
      });

      // Merge dailyModelCosts
      if (entry.dailyModelCosts) {
        if (!aggregated.dailyModelCosts) aggregated.dailyModelCosts = {};
        Object.entries(entry.dailyModelCosts).forEach(([date, models]) => {
          if (!aggregated.dailyModelCosts![date]) aggregated.dailyModelCosts![date] = {};
          Object.entries(models).forEach(([model, cost]) => {
            aggregated.dailyModelCosts![date][model] = (aggregated.dailyModelCosts![date][model] ?? 0) + cost;
          });
        });
      }

      if (entry.firstSeen && (!earliest || entry.firstSeen < earliest)) {
        earliest = entry.firstSeen;
      }
      if (entry.lastSeen && (!latest || entry.lastSeen > latest)) {
        latest = entry.lastSeen;
      }
      if (entry.days > maxDays) {
        maxDays = entry.days;
      }
      if (entry.status === 'Active') {
        isActive = true;
      }
    });

    aggregated.requests = aggregated.numberOfRequests;
    aggregated.cachedPercentage = aggregated.contextTokens > 0
      ? (aggregated.cachedTokens / aggregated.contextTokens) * 100
      : 0;
    aggregated.costPer100Requests = aggregated.numberOfRequests > 0
      ? (aggregated.cost / aggregated.numberOfRequests) * 100
      : 0;
    aggregated.days = maxDays;
    if (earliest) aggregated.firstSeen = earliest;
    if (latest) aggregated.lastSeen = latest;
    aggregated.status = isActive ? 'Active' : 'Inactive';

    const providerValues = Array.from(providers).filter(Boolean) as Array<NonNullable<ApiKeyUsageDetail['provider']>>;
    if (providerValues.length === 1) {
      aggregated.provider = providerValues[0];
    } else if (providerValues.length === 0) {
      aggregated.provider = undefined;
    } else {
      aggregated.provider = 'mixed';
    }

    aggregated.projectIds = workspaceIds.size > 0 ? Array.from(workspaceIds) : undefined;
    aggregated.projectId = aggregated.projectIds && aggregated.projectIds.length === 1
      ? aggregated.projectIds[0]
      : 'multiple-workspaces';

    if (workspaceNames.size > 0) {
      aggregated.projectName = workspaceNames.size === 1
        ? Array.from(workspaceNames)[0]
        : Array.from(workspaceNames).join(', ');
    }

    aggregated.sourceIds = Array.from(sourceIds);

    // Preserve ownerUserId if all entries share the same one
    const ownerIds = new Set(group.map(e => e.ownerUserId).filter(Boolean));
    if (ownerIds.size === 1) {
      aggregated.ownerUserId = Array.from(ownerIds)[0];
    }

    // Store original entries for split-by-source view in expanded details
    aggregated.mergedSources = group;

    merged.push(aggregated);
  });

  return merged;
}
